import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { UtilityService } from '@nttp/common-service/utility-service';
import { VerifyPasscodeComponent } from '@pg-app/client/manage-recipient/containers/verify-passcode/verify-passcode.component';
import { NewRecipientModel } from '@pg-app/client/manage-recipient/models/recipient.model';
import { RecipientInfoService } from '@pg-app/client/manage-recipient/services/recipient-info.service';
import { RecipientService } from '@pg-app/client/manage-recipient/services/recipient.service';
import { environment } from '@pg-env/environment';
import { ToastrService } from 'ngx-toastr';
import { forkJoin, Observable } from 'rxjs';
import { RegistrationStep2Service } from '@pg-app/client/services/registrationStep2.service';
import { MatRadioChange } from '@angular/material/radio';
import { CommonDialogComponent } from '@pg-app/common/common-dialog/common-dialog.component';
import { startWith, debounceTime, map } from 'rxjs/operators';
import { SendPaymentInstructionService } from '@pg-app/client/send-payment-instruction/services/send-payment-instruction.service';
import { ShareService } from '@nttp/common-service/share-service';
import { UserRoleEnum } from '@nttp/enum/user-role.enum';
import { DialogTypeEnum } from '@nttp/enum/common-dialog-type.enum';
import { swiftCodeValidator } from '../constants/swift-code-validator';
import { Regex } from '@pg-app/shared/constants/regex';
import { SortTypeEnum } from '@pg-app/shared/enums/sort-type.enum';
import { countryCodes, currencyCodes } from '../constants/add-recipient-constants';
import { BeneficiaryTypes } from '@pg-app/client/payment/constants/beneficiary-type-list';
import { BankAccountTypes } from '@pg-app/client/payment/constants/bank-account-type-list';
import { fasterPayGbpBankDetailsValidator } from '../constants/fasterpay-gbp-validator';
import { swiftOrBankCodeValidator } from '../constants/swift-bank-validator';
import { numberOfWordBeneficiaryNameValidator } from '../constants/beneficiary-name-number-of-word-validator';
import { SendPaymentInstructionModelService } from '@pg-app/client/send-payment-instruction/services/send-payment-instruction.model.service';
import { RemitterTypeEnum, RemitterTypes } from '@pg-app/client/payment/constants/remitter-type';
import { BeneficiaryColumns, RemitterColumn } from '../constants/benficiary-table-column-constant';
import { BankingTypes } from '@pg-app/shared/constants/banking-types-constants';
import { BeneficiarySourceEnum } from '@pg-app/shared/enums/beneficiary-source.enum';

export interface Currency {
  id: string;
  name: string;
  code: string;
}

@Component({
  selector: 'np-manage-recipient',
  templateUrl: './manage-recipient.component.html',
  styleUrls: ['./manage-recipient.component.scss']
})
export class ManageRecipientComponent implements OnInit {
  recipientInfo: FormGroup;
  bankInfo: FormGroup;
  paymentInfo: FormGroup;
  searchRecipient: FormGroup;

  countries = [];
  currencies: Currency[];
  banks = [];
  display = false;
  isPEPSanctions = false;

  rows: Array<any> = [];
  columns = JSON.parse(JSON.stringify(BeneficiaryColumns));

  totalItems = 0;
  initialPage = 1;

  isIBANRequired = false;
  isFasterPayandGBP = false;
  isFasterPay = false;
  isBankCodeAndAccountMandatory = false;

  // Purpose of payment
  purposeOfPayments = [];
  purposeOfPaymentsPipe: Observable<Array<any>>;
  purposeofPaymentSelected: any;

  userRoleEnum = UserRoleEnum;
  mandateValue: boolean;
  currentMandateId = null;
  beneTypes = BeneficiaryTypes;
  bankAccountTypes = BankAccountTypes;
  validationOk = true;
  validatedBankDetails = null;
  isBankCode9Digits = false;
  purposeOfPaymentId = null;

  // invalid selected beneficiary from send payment
  beneficiaryId: string;

  // check if client press continue button or not
  clickedContinueBtn = false;
  remitterRequired: boolean;

  isLoading = true;

  get recipientNameCtrl(): FormControl {
    return this.recipientInfo.get('recipientName') as FormControl;
  }

  get countryCtrl(): FormControl {
    return this.recipientInfo.get('country') as FormControl;
  }

  get currencyCtrl(): FormControl {
    return this.recipientInfo.get('currency') as FormControl;
  }

  get emailCtrl(): FormControl {
    return this.recipientInfo.get('email') as FormControl;
  }

  get mandateCtrl(): FormControl {
    return this.recipientInfo.get('mandate') as FormControl;
  }

  get bankNameCtrl(): FormControl {
    return this.bankInfo.get('bankName') as FormControl;
  }

  get address1Ctrl(): FormControl {
    return this.bankInfo.get('address1') as FormControl;
  }

  get address2Ctrl(): FormControl {
    return this.bankInfo.get('address2') as FormControl;
  }

  get address3Ctrl(): FormControl {
    return this.bankInfo.get('address3') as FormControl;
  }

  get cityCtrl(): FormControl {
    return this.bankInfo.get('city') as FormControl;
  }

  get stateCtrl(): FormControl {
    return this.bankInfo.get('state') as FormControl;
  }

  get zipCodeCtrl(): FormControl {
    return this.bankInfo.get('zipCode') as FormControl;
  }

  get bankCountryCtrl(): FormControl {
    return this.bankInfo.get('bankCountry') as FormControl;
  }

  get referenceCtrl(): FormControl {
    return this.bankInfo.get('reference') as FormControl;
  }

  get purposeOfPaymentCtrl(): FormControl {
    return this.bankInfo.get('purposeOfPayment') as FormControl;
  }

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private utilityService: UtilityService,
    private recipientService: RecipientService,
    public dialog: MatDialog,
    public recipientInfoService: RecipientInfoService,
    private toastr: ToastrService,
    private newRecipientModel: NewRecipientModel,
    public registrationService: RegistrationStep2Service,
    private sendPaymentInstructionService: SendPaymentInstructionService,
    private shareService: ShareService,
    private route: ActivatedRoute,
    private paymentModel: SendPaymentInstructionModelService
  ) {
    this.beneficiaryId = this.route.snapshot.queryParams.id;
  }

  ngOnInit() {
    this.searchRecipient = this.fb.group({
      searchText: ''
    });

    this.recipientInfo = this.fb.group({
      recipientName: ['', [Validators.required, Validators.maxLength(255), Validators.pattern(Regex.swiftAndSquareBracket)]],
      country: ['', Validators.required],
      recipientCountry: [''],
      recipientAddress1: ['', [Validators.maxLength(35), Validators.pattern(Regex.nonSwiftCharacters)]],
      recipientAddress2: ['', [Validators.maxLength(35), Validators.pattern(Regex.nonSwiftCharacters)]],
      currency: ['', Validators.required],
      email: ['', [Validators.email, Validators.maxLength(255)]],
      mandate: ['', Validators.required],
      beneType: ['', Validators.required],
      beneCity: ['', [Validators.maxLength(60), Validators.pattern(Regex.nonSwiftCharacters)]],
      beneState: ['', [Validators.maxLength(50), Validators.pattern(Regex.nonSwiftCharacters)]],
      benePostCode: ['', [Validators.maxLength(12), Validators.pattern(Regex.nonSwiftCharacters)]],
      beneTaxId: ['', Validators.pattern(Regex.nonSwiftCharacters)],
    },
      {
        validator: numberOfWordBeneficiaryNameValidator()
      });

    this.bankInfo = this.fb.group({
      bankName: [''],
      address1: [''],
      address2: [''],
      address3: [''],
      city: [''],
      state: [''],
      zipCode: [''],
      bankCountry: [''],
      reference: ['', [Validators.maxLength(70), Validators.pattern(Regex.nonSwiftCharacters)]],
      purposeOfPayment: [''],
      bankAccountType: '',
    });

    this.newRecipientModel.clear();

    this.getRecipients();

    // prepare all data
    const getCountries = this.shareService.countryList$;
    const getCurrencies = this.utilityService.getCurrencies();
    const getBankingConfig = this.recipientService.getBankingConfig();

    forkJoin([getCountries, getCurrencies, getBankingConfig]).subscribe(res => {
      this.newRecipientModel.bankConfig = res[2];
      this.getCountries(res[0]);
      this.getCurrencies(res[1]);
      if (this.beneficiaryId) {
        this.getBeneficiaryDetails(this.beneficiaryId);
      }
    });
  }

  checkedItemChange(event) {
    if (event.checked) {
      const rowSelected = this.rows.filter(item => item.checked);
      if (rowSelected.length > 1) {
        this.toastr.error('You can only select one beneficiary at a time');
      }
      const preCheckedRow = rowSelected.filter(item => item.id !== event.data.id);
      preCheckedRow.forEach(p => {
        p.checked = false;
      });
    }
  }

  clickSetAsMandateBtn() {
    const rowSelected = this.rows.find(item => item.checked);

    if (!rowSelected) {
      this.toastr.error('No beneficiary was selected.');
    } else if (rowSelected.paymentMandate.toLowerCase() === 'yes') {
      this.toastr.error(`${rowSelected.recipientName} is already a mandate for ${rowSelected.currency}!`);
      rowSelected.checked = false;
    } else {
      this.recipientService.getUniqueRecipientPerCurrency(rowSelected.currency).subscribe(res => {
        if (res.receipient) {
          this.showDialog(res.receipient, rowSelected, true);
        } else {
          this.setAsMandate(rowSelected);
        }
      });
    }
  }

  changeMandate(event: MatRadioChange) {
    if (event.value === 'Yes') {
      const buyCurrency = this.currencyCtrl.value;
      const fullBuyCurrency = this.currencies.find(x => x.id === buyCurrency);
      this.recipientService.getUniqueRecipientPerCurrency(fullBuyCurrency.code).subscribe(res => {
        if (res && res.receipient) {
          const receipient = {
            id: this.recipientInfoService.recipientId,
            name: this.recipientNameCtrl.value,
            currency: fullBuyCurrency.code
          };
          this.showDialog(res.receipient, receipient);
        } else {
          this.mandateCtrl.setValue(event.value);
          this.currentMandateId = null;
        }
      });
    } else if (event.value === 'No') {
      this.mandateCtrl.setValue(event.value);
    }
  }

  setAsMandate(rowSelected) {
    const tempObject = {
      MandateRecipient: rowSelected.id,
      CurrencyCodeToRemoveMandate: rowSelected.currency
    };
    this.recipientService.SetRecipientAsMandate(tempObject).subscribe(
      res => {
        this.toastr.success(`Set ${rowSelected.recipientName} as mandate successfully!`);
        const newMandateindex = this.rows.findIndex(x => x.id === res.recipientMandate.id);
        this.rows[newMandateindex].paymentMandate = res.recipientMandate.paymentMandate ? 'Yes' : 'No';
        this.rows[newMandateindex].lastUpdatedDate = res.recipientMandate.updatedDate;
        this.rows[newMandateindex].updatedBy = res.updatedByName;

        if (res.previousRecipientMandate !== null) {
          const previousMandateIndex = this.rows.findIndex(x => x.id === res.previousRecipientMandate.id);
          if (newMandateindex !== previousMandateIndex) {
            this.rows[previousMandateIndex].paymentMandate = res.previousRecipientMandate.paymentMandate ? 'Yes' : 'No';
            this.rows[previousMandateIndex].lastUpdatedDate = res.previousRecipientMandate.updatedDate;
            this.rows[previousMandateIndex].updatedBy = res.updatedByName;
          }
        }
      }
    );
  }

  public showDialog(currentMandate, selectedRecipient, setMandateDirectly = false) {
    const dialogRef = this.dialog.open(CommonDialogComponent, {
      disableClose: true,
      data: {
        title: `A mandate already exists for ${selectedRecipient.currency} to the following beneficiary: ${currentMandate.name}.<br class = "d-md-block d-none"> Would you like to replace the current mandate with this beneficiary?`,
        dialogType: DialogTypeEnum.Confirm
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && setMandateDirectly) {
        this.setAsMandate(selectedRecipient);
        const recToRemove = {
          id: currentMandate.id
        };
        this.recipientService.RemoveRecipientMandateTag(recToRemove).subscribe();
      } else if (result && !setMandateDirectly) {
        this.mandateCtrl.setValue('Yes');
        this.currentMandateId = currentMandate.id;
      } else if (!result && !setMandateDirectly) {
        this.currentMandateId = null;
        this.mandateCtrl.setValue('No');
      } else {
        selectedRecipient.checked = false;
      }
    });
  }

  // Call every when change bank country and currency
  checkRule(data = null) {
    const bankConfig = this.newRecipientModel.bankConfig;
    const currency = this.currencies.find(p => p.code.toLowerCase() === this.currencyCtrl.value.toLowerCase());
    const country = this.countries.find(item => item.name.toLowerCase() === this.countryCtrl.value.toLowerCase());

    this.paymentInfo = this.fb.group({
      sortCode: ['', [Validators.pattern(Regex.sixDigitPattern)]],
      accountNumber: [''],
      iban: [''],
      bankCode: [''],
      swiftCode: ['', [Validators.pattern(Regex.swiftCodePattern)]],
    });

    this.getBankingType(bankConfig, country, currency);

    if (data) {
      this.bindingPaymentDetail(data);
    }

    this.paymentInfo.valueChanges.pipe(debounceTime(450)).subscribe(() => {
      this.validationOk = false;
      this.resetBankDetailForm();
    });
  }

  getCountries(data) {
    data.forEach(item => {
      const flagIcon = (item.code.length <= 2 ? item.code : item.code.substring(0, 2)).toLowerCase();
      item.flag = `flag-icon flag-icon-${flagIcon}`;
    });
    this.countries = data || [];
  }

  getCurrencies(data) {
    data.forEach(item => {
      const flagIcon = (item.code.length <= 2 ? item.code : item.code.substring(0, 2)).toLowerCase();
      item.flag = `flag-icon flag-icon-${flagIcon}`;
    });
    this.currencies = data || [];
  }

  getRecipients(paging = null) {
    const filters = paging?.filter && paging.filter.length > 0 ? paging.filter : null;
    if (filters) {
      const filterAccountNumberIban = filters.find(item => item.propertyName === 'AccountNumberIban');
      if (filterAccountNumberIban) {
        filters.splice(filters.indexOf(filterAccountNumberIban), 1);
        filters.push({
          propertyName: 'AccountNumber',
          propertyValue: filterAccountNumberIban.propertyValue
        });
        filters.push({
          propertyName: 'Iban',
          propertyValue: filterAccountNumberIban.propertyValue
        });
      }

      const filterPaymentMandate = filters.find(item => item.propertyName === 'PaymentMandate');
      if (filterPaymentMandate) {
        filterPaymentMandate.propertyName = 'PaymentMandateText';
      }
    }
    const getRecipientByName = {
      clientId: null,
      name: this.searchRecipient.get('searchText').value,
      pageSize: environment.defaultPageSize,
      pageIndex: paging ? paging.currentPage : 1,
      sortName: paging?.sortName || 'LastUpdatedDate',
      sortType: paging?.sortName ? paging.sortType : SortTypeEnum.Desc,
      filters: filters || null
    };

    this.recipientService.getRecipientList(getRecipientByName).subscribe(
      res => {
        const data = res.receipients || [];
        if (data && data.length > 0) {
          data.forEach(item => {
            item.accountNumberIban = item.iban && item.accountNumber ? item.accountNumber + '/' + item.iban : item.iban || item.accountNumber;
            item.paymentMandate = item.paymentMandate ? 'Yes' : 'No';
          });
        }
        this.rows = data;
        this.totalItems = res.pagination.totalItems;
        this.initialPage = res.pagination.pageIndex;

        this.isLoading = false;
      },
    );
  }

  addNewRecipient() {
    this.router.navigate(['client/manage-recipient/add-new-recipient']);
  }

  updateRecipient() {
    if (!this.recipientInfo.valid || !this.bankInfo.valid || !this.paymentInfo.valid || !this.validationOk) {
      return;
    }

    if (this.purposeOfPayments.length > 0 && !this.purposeofPaymentSelected) {
      return;
    }

    if (!this.clickedContinueBtn) {
      this.validate(true);
    } else {
      this.confirmUpdatePayment();
    }
  }

  public deleteRecipient() {
    const dialogDelete = this.dialog.open(CommonDialogComponent, {
      data: {
        title: 'Are you sure to remove this beneficiary?',
        dialogType: 3
      },
      disableClose: true
    });
    dialogDelete.afterClosed().subscribe(res => {
      if (res) {
        this.confirmDelete();
      }
    });
  }

  private confirmUpdatePayment() {
    const currency = this.currencies.find(p => p.code === this.recipientInfo.get('currency').value);
    const bankCountry = this.countries.find(p => p.name === this.countryCtrl.value);
    const beneCountry = this.countries.find(p => p.name === this.recipientInfo.get('recipientCountry').value);

    const receipient = {
      id: this.recipientInfoService.recipientId,
      name: this.recipientNameCtrl.value,
      countryId: beneCountry ? beneCountry.id : null,
      address1: this.recipientInfo.get('recipientAddress1').value || null,
      address2: this.recipientInfo.get('recipientAddress2').value || null,
      currencyId: currency.id,
      emailAddress: this.recipientInfo.get('email').value || null,
      paymentMandate: this.mandateCtrl.value === 'Yes' ? true : false,
      status: 1,
      isNotified: this.recipientInfo.get('email').value ? true : false,
      isPEPSanctions: this.isPEPSanctions,
      beneficiaryType: this.recipientInfo.get('beneType').value,
      beneficiaryCity: this.recipientInfo.get('beneCity').value || null,
      beneficiaryStateProvince: this.recipientInfo.get('beneState').value || null,
      beneficiaryPostCode: this.recipientInfo.get('benePostCode').value || null,
      beneficiaryTaxId: this.recipientInfo.get('beneTaxId').value || null,
    };
    const mainBank = {
      id: this.recipientInfoService.mainBankId,
      name: this.validatedBankDetails.bankName,
      accountNumber: this.validatedBankDetails.accountNumber || null,
      address1: this.validatedBankDetails.address1,
      address2: this.validatedBankDetails.address2 || null,
      address3: this.validatedBankDetails.address3 || null,
      city: this.validatedBankDetails.city || null,
      province: this.validatedBankDetails.stateProvince || null,
      postCode: this.validatedBankDetails.zipPostalCode || null,
      countryId: bankCountry ? bankCountry.id : null,
      iBan: this.validatedBankDetails.IBAN || null,
      sWiftCode: this.validatedBankDetails.SWIFTCode || null,
      sortCodeUK: this.validatedBankDetails.sortCode || null,
      bankCode: this.validatedBankDetails.bankCode || null,
      status: 1,
      reference: this.bankInfo.get('reference').value || null,
      purposeOfPaymentId: this.purposeofPaymentSelected ? this.purposeofPaymentSelected.id : null,
      bankAccountType: this.bankInfo.get('bankAccountType').value,
    };

    const dialogUpdate = this.dialog.open(VerifyPasscodeComponent, {
      data: {
        isIntermediary: false,
        receipient: receipient,
        mainBank: mainBank,
        intermediaryBank: null,
        clientId: null
      },
      width: '585px',
      panelClass: 'verify-passcode-dialog'
    });
    dialogUpdate.afterClosed().subscribe(result => {
      if (result) {
        if (this.mandateCtrl.value === 'Yes' && this.currentMandateId) {
          const recToRemove = {
            id: this.currentMandateId
          };
          this.recipientService.RemoveRecipientMandateTag(recToRemove).subscribe(() => {
            this.currentMandateId = null;
            this.getRecipients();
          });
        } else {
          this.getRecipients();
        }
        this.recipientInfoService.recipientId = result;
        this.toastr.success(`Update beneficiary successfully!`);
      }
    });
  }

  private confirmDelete() {
    const param = {
      id: this.recipientInfoService.recipientId,
      clientId: null,
      name: null
    };
    this.recipientService.deleteRecipientBanks(param).subscribe(
      res => {
        this.display = false;
        this.toastr.success(`Delete beneficiary successfully!`);
        this.getRecipients();
      }
    );
  }

  getBeneficiaryDetails(data) {
    const beneficiaryId = data?.row?.id || data;

    // In case update beneficiary from Send payment
    // and update another one
    // Should not return send payment instruction
    if (this.paymentModel.manageRecipientFromPayment && data?.row?.id) {
      this.paymentModel.manageRecipientFromPayment = false;
    }

    if (beneficiaryId !== null) {
      const param = {
        id: beneficiaryId,
        clientId: null
      };
      this.recipientInfoService.recipientId = beneficiaryId;
      this.currentMandateId = null;
      this.display = false;

      this.recipientService.getRecipientById(param).subscribe(res => {
        if (res) {
          this.bindingRecipientDetail(res.receipient);
          this.bindingBankDetail(res.banks);

          // check fields mandatory
          this.checkRule(res.banks);
          this.checkRequiredBeneficiaryDetails();
          this.checkRequiredStateProvince();
          this.checkRequiredPurposeOfPayments();

          this.display = true;
          setTimeout(() => {
            this.paymentInfo.markAllAsTouched();
            this.bankInfo.markAllAsTouched();
            this.recipientInfo.markAllAsTouched();
            window.scrollTo({
              top: document.body.scrollHeight,
              behavior: 'smooth'
            });
          });
        }
      });
    }
  }

  private bindingPaymentDetail(bank: any) {
    const mainBank = bank.find(item => !item.isIntermediary);
    if (mainBank) {
      this.paymentInfo.patchValue({
        sortCode: mainBank.sortCodeUK,
        iban: mainBank.iBan,
        accountNumber: mainBank.accountNumber,
        swiftCode: mainBank.sWiftCode,
        bankCode: mainBank.bankCode,
      });
      this.validationOk = true;
    }
  }

  private bindingRecipientDetail(receipient: any) {
    if (receipient) {
      const currency = this.currencies.find(p => p.id === receipient.currencyId);
      const beneCountry = this.countries.find(p => p.id === receipient.countryId);
      this.recipientInfo.patchValue({
        recipientName: receipient.name,
        currency: currency ? currency.code : null,
        email: receipient.emailAddress,
        recipientCountry: beneCountry ? beneCountry.name : null,
        recipientAddress1: receipient.address1,
        recipientAddress2: receipient.address2,
        mandate: receipient.paymentMandate ? 'Yes' : 'No',
        beneType: receipient.beneficiaryType,
        beneCity: receipient.beneficiaryCity,
        beneState: receipient.beneficiaryStateProvince,
        benePostCode: receipient.beneficiaryPostCode,
        beneTaxId: receipient.beneficiaryTaxId,
      });

      this.recipientInfoService.recipientStatus = receipient.status;
      this.recipientInfoService.isNotified = receipient.isNotified;
      this.isPEPSanctions = receipient.isPEPSanctions;
    }
  }

  private bindingBankDetail(banks: any) {
    if (!banks || banks.length <= 0) {
      return;
    }

    const mainBank = banks.find(item => !item.isIntermediary);
    const bankCountry = this.countries.find(p => p.id === mainBank.countryId);

    this.countryCtrl.setValue(bankCountry ? bankCountry.name : null);

    this.bankInfo.patchValue({
      bankName: mainBank.name,
      address1: mainBank.address1,
      address2: mainBank.address2,
      address3: mainBank.address3,
      city: mainBank.city,
      state: mainBank.province,
      zipCode: mainBank.postCode,
      bankCountry: mainBank.countryId,
      reference: mainBank.reference,
      bankAccountType: mainBank.bankAccountType
    });
    this.purposeOfPaymentId = mainBank.purposeOfPaymentId;

    this.recipientInfoService.mainBankId = mainBank.id;
    this.recipientInfoService.mainBankStatus = mainBank.status;
    this.recipientInfoService.mainReference = mainBank.reference;
    this.recipientInfoService.isIntermediary = mainBank.isIntermediary;

    this.validatedBankDetails = {
      IBAN: mainBank.iBan,
      sortCode: mainBank.sortCodeUK,
      accountNumber: mainBank.accountNumber,
      bankCode: mainBank.bankCode,
      SWIFTCode: mainBank.sWiftCode,

      bankName: mainBank.name,
      address1: mainBank.address1,
      address2: mainBank.address2,
      address3: mainBank.address3,
      city: mainBank.city,
      stateProvince: mainBank.province,
      zipPostalCode: mainBank.postCode,
      reference: mainBank.reference,

      isSendOneOffPayment: undefined,
      BIC: undefined,
      mandate: undefined,
      purposeOfPayment: undefined
    };

  }

  // Ticket 2925 - Apply Financial
  validate(updateBeneficiary = false) {
    this.paymentInfo.markAllAsTouched();
    if (!this.paymentInfo.valid) {
      return;
    }

    const iBan = this.paymentInfo.get('iban').value;
    const bankCode = this.paymentInfo.get('bankCode').value;
    const swiftCode = this.paymentInfo.get('swiftCode').value;
    const accountNumber = this.paymentInfo.get('accountNumber').value;
    const sortCode = this.paymentInfo.get('sortCode').value;

    const countryCode = this.countries.find(p => p.name.toLowerCase() === this.recipientInfo.get('country').value.toLowerCase()).code;
    const currencyCode = this.recipientInfo.get('currency').value.toUpperCase();

    let param = {
      countryCode: countryCode,
      currencyCode: currencyCode,
      accountNumber: null,
      sortCode: null,
      bankCode: null,
      iban: null,
      swiftCode: null
    }

    if (this.isFasterPayandGBP) {
      if (iBan) {
        param.iban = iBan;
      } else {
        param.accountNumber = accountNumber;
        param.sortCode = sortCode;
      }
    } else if (this.isIBANRequired) {
      param.iban = iBan;
    } else if (!this.isBankCodeAndAccountMandatory) {
      param.accountNumber = accountNumber;
      if (bankCode) {
        param.bankCode = bankCode;
      } else {
        param.swiftCode = swiftCode;
      }
    } else {
      param.accountNumber = accountNumber;
      param.bankCode = bankCode;
    }

    this.recipientService.validate(param).subscribe(
      data => {
        if (!data) {
          this.toastr.error(`Unable to validate. Please check the details and try again`);
          return;
        }

        if (data.CountryCode !== countryCode) {
          this.toastr.error('Incorrect bank country. Please correct the bank country to ' + data.countryCode);
          return;
        }

        this.bankInfo.patchValue({
          bankName: data.Bank_Name,
          address1: data.Address1,
          address2: data.Address2,
          city: data.City,
          state: data.State_Province,
          zipCode: data.Zip_Postal
        });
        this.savingBankDetails(data);

        this.validationOk = true;
        if (updateBeneficiary) {
          this.confirmUpdatePayment();
        }
      },
      err => {
        this.validationOk = false;
        this.validatedBankDetails = undefined;
      }
    );
  }

  private savingBankDetails(data) {
    this.validatedBankDetails = {
      IBAN: data.IBan,
      sortCode: data.SortCode,
      accountNumber: data.AccountNumber,
      bankCode: data.BankCode,
      SWIFTCode: data.SwiftCodeBic,

      bankName: data.Bank_Name,
      address1: data.Address1,
      address2: data.Address2,
      address3: data.Address3,
      city: data.City,
      stateProvince: data.State_Province,
      zipPostalCode: data.Zip_Postal,
      reference: data.Reference,

      isSendOneOffPayment: undefined,
      BIC: undefined,
      mandate: undefined,
      purposeOfPayment: undefined
    };
  }

  checkRequiredPurposeOfPayments() {
    const bankName = this.recipientInfo.get('country').value;
    const bankCountry = this.countries.find(p => bankName && p.name.toLowerCase() === bankName.toLowerCase());
    this.bankInfo.get('bankCountry').setValue(bankCountry.id);
    const currencyCode = this.recipientInfo.get('currency').value;
    this.getPurposeOfPayments(bankCountry?.id, currencyCode);
  }

  private filterSearch(): any[] {
    const purposeOfPayment = this.bankInfo.get('purposeOfPayment').value;
    const filterValue = !purposeOfPayment ? '' : purposeOfPayment.toLowerCase();

    const pp = this.purposeOfPayments.find(x => {
      const combineValue = `${x.code} - ${x.description}`;
      return combineValue.toLowerCase() === filterValue;
    });
    this.purposeofPaymentSelected = pp ? pp : null;

    return this.purposeOfPayments.filter(x => {
      const combineValue = `${x.code} - ${x.description}`;
      return combineValue.toLowerCase().indexOf(filterValue) !== -1;
    });
  }

  private getPurposeOfPayments(countryId: any, currencyCode: any) {
    if (currencyCode == 'CNY') {
      this.sendPaymentInstructionService.getPaymentCodesForCNY().subscribe(data => {
        this.purposeOfPayments = data;
        this.updateValueAndValidityForPurposeOfPayment();
      });
    } else if (countryId) {
      this.sendPaymentInstructionService.getPaymentCodes(countryId).subscribe(data => {
        this.purposeOfPayments = data;
        this.updateValueAndValidityForPurposeOfPayment();
      });
    }
  }

  private updateValueAndValidityForPurposeOfPayment() {
    if (this.purposeOfPayments.length > 0) {
      this.bankInfo.get('purposeOfPayment').setValidators([Validators.required]);
      this.purposeOfPaymentsPipe = this.bankInfo.get('purposeOfPayment').valueChanges.pipe(
        startWith(''),
        debounceTime(350),
        map(value => this.filterSearch())
      );

      // If a purpose of payment id supplied, use it to fill the control and variable
      if (this.purposeOfPaymentId) {
        this.selectPurposeOfPayment(this.purposeOfPaymentId);
      }
    } else {
      this.bankInfo.get('purposeOfPayment').clearValidators();
    }
    this.bankInfo.get('purposeOfPayment').updateValueAndValidity();
  }

  public selectPurposeOfPayment(purposeOfPaymentId: any): void {
    const purposeOfPayment = this.purposeOfPayments.find(x => x.id === purposeOfPaymentId);
    if (purposeOfPayment) {
      this.purposeofPaymentSelected = purposeOfPayment; // Save the object
      this.purposeOfPaymentCtrl.setValue(purposeOfPayment.code + ' - ' + purposeOfPayment.description); // For display purposes only
    }
  }

  checkRequiredBeneficiaryDetails() {
    const currencyCode = this.recipientInfo.get('currency').value.toUpperCase();
    const countryName = this.recipientInfo.get('country').value;
    const country = this.countries.find(p => p.name.toLowerCase() === countryName.toLowerCase());

    if (!currencyCode || !country) {
      return;
    }

    // require beneficiary Tax Id get from BE config
    const beneTaxIdMandatory = !!this.newRecipientModel.bankConfig.requireTaxId.find(p => p.countryCode === country.code && p.currencyCode === currencyCode);
    if (beneTaxIdMandatory) {
      this.recipientInfo.get('beneTaxId').setValidators([Validators.required, Validators.pattern(Regex.nonSwiftCharacters)]);
    } else {
      this.recipientInfo.get('beneTaxId').setValidators(Validators.pattern(Regex.nonSwiftCharacters));
    }

    this.recipientInfo.get('beneTaxId').updateValueAndValidity();
  }

  checkRequiredStateProvince() {
    const countryName = this.recipientInfo.get('country').value.toLowerCase();
    const country = this.countries.find(p => p.name.toLowerCase() === countryName);
    if (!country) {
      return;
    }

    // require beneficiary state or province get from BE config
    if (this.newRecipientModel.bankConfig.requireStateProvince.includes(country.code)) {
      this.recipientInfo.get('beneState').setValidators([Validators.required, Validators.maxLength(50), Validators.pattern(Regex.nonSwiftCharacters)]);
    } else {
      this.recipientInfo.get('beneState').setValidators([Validators.maxLength(50), Validators.pattern(Regex.nonSwiftCharacters)]);
    }
    this.recipientInfo.get('beneState').updateValueAndValidity();
  }

  resetBankDetailForm() {
    this.bankInfo.patchValue({
      bankName: '',
      address1: '',
      address2: '',
      address3: '',
      city: '',
      state: '',
      zipCode: '',
    });
  }

  getBankingConfig() {
    this.recipientService.getBankingConfig().subscribe(res => {
      this.newRecipientModel.bankConfig = res;
    });
  }

  getBankingType(bankConfig, country, currency) {
    let dto = {
      countryId: country.id,
      currencyId: currency.id
    };
    this.shareService.getBankingType(dto).subscribe(bankingType => {
      this.isFasterPay = country.isFasterPay;
      this.isFasterPayandGBP = bankingType === BankingTypes.Case1_FasterPay_GBP;
      this.isIBANRequired = bankingType === BankingTypes.Case2_IBAN;
      this.isBankCodeAndAccountMandatory = bankingType === BankingTypes.Case3_BankCode;
      this.isBankCode9Digits = !!bankConfig.require9DigitBankCode.find(p => p.countryCode === country.code && p.currencyCode === currency.code);

      const bankAccountTypeMandatory = !!bankConfig.requireAccountType.find(p => p.countryCode === country.code && p.currencyCode);
      const accountNumberPattern = this.isFasterPay ? Regex.eightDigitPattern : Regex.alphabetPattern;
      const bankCodePattern = this.isBankCode9Digits ? Regex.nineDigitPattern : Regex.alphabetPattern;
      const bankAccountTypeValidators = bankAccountTypeMandatory ? [Validators.required] : [];

      // form validation
      const formValidator = this.isFasterPayandGBP ? fasterPayGbpBankDetailsValidator() :
        !this.isFasterPayandGBP && !this.isIBANRequired && !this.isBankCodeAndAccountMandatory ?
          [
            swiftCodeValidator(),
            swiftOrBankCodeValidator()
          ] : [];

      // iban validation
      const ibanValidator = this.isIBANRequired ?
        [
          Validators.pattern(Regex.alphabetAndSpacePattern),
          Validators.maxLength(34), Validators.minLength(15),
          Validators.required
        ]
        :
        [
          Validators.pattern(Regex.alphabetAndSpacePattern),
          Validators.maxLength(34), Validators.minLength(15)
        ];

      // account number validation
      const accountNumberValidator = !this.isFasterPayandGBP && !this.isIBANRequired ?
        [
          Validators.pattern(accountNumberPattern),
          Validators.required
        ] :
        [Validators.pattern(accountNumberPattern)];

      // bank code validation
      const bankCodeValidation = this.isBankCodeAndAccountMandatory ?
        [
          Validators.pattern(bankCodePattern),
          Validators.maxLength(50),
          Validators.required
        ] :
        [
          Validators.pattern(bankCodePattern),
          Validators.maxLength(50),
        ];

       this.paymentInfo.get('accountNumber').setValidators(accountNumberValidator);
       this.paymentInfo.get('iban').setValidators(ibanValidator);
       this.paymentInfo.get('bankCode').setValidators(bankCodeValidation);
       this.paymentInfo.setValidators(formValidator);
       this.bankInfo.get('bankAccountType').setValidators(bankAccountTypeValidators);

      // GPB to UK or EUR to Euro zone country
      // Address1, city, post code, recipient country are optional
      if (this.isFasterPayandGBP || (currency.code === currencyCodes.eur && country.isEurozone)) {
        this.recipientInfo.get('recipientAddress1').setValidators([Validators.maxLength(35), Validators.pattern(Regex.nonSwiftCharacters)]);
        this.recipientInfo.get('beneCity').setValidators([Validators.maxLength(60), Validators.pattern(Regex.nonSwiftCharacters)]);
        this.recipientInfo.get('benePostCode').setValidators([Validators.maxLength(12), Validators.pattern(Regex.nonSwiftCharacters)]);
        this.recipientInfo.get('recipientCountry').clearValidators();
      } else {
        this.recipientInfo.get('recipientAddress1').setValidators([Validators.required, Validators.maxLength(35), Validators.pattern(Regex.nonSwiftCharacters)]);
        this.recipientInfo.get('beneCity').setValidators([Validators.required, Validators.maxLength(60), Validators.pattern(Regex.nonSwiftCharacters)]);
        this.recipientInfo.get('benePostCode').setValidators([Validators.required, Validators.maxLength(12), Validators.pattern(Regex.nonSwiftCharacters)]);
        this.recipientInfo.get('recipientCountry').setValidators(Validators.required);
      }
      this.recipientInfo.get('recipientAddress1').updateValueAndValidity();
      this.recipientInfo.get('beneCity').updateValueAndValidity();
      this.recipientInfo.get('benePostCode').updateValueAndValidity();
      this.recipientInfo.get('recipientCountry').updateValueAndValidity();
    });
  }

}
